import { useState } from 'react';
import { Center, Loader } from '@mantine/core';
import { DrawnSignatureSource } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { SavedSignaturePicker } from '@dealstack/frontend/shared/components/Signature/SavedSignaturePicker/SavedSignaturePicker';
import { SignatureEditor } from '@dealstack/frontend/shared/components/Signature/SignatureEditor/SignatureEditor';
import { useSavedSignature } from '@dealstack/frontend/shared/components/Signature/hooks/useSavedSignature';
import { useSaveUserSignature } from '@dealstack/frontend/shared/components/Signature/hooks/useSaveUserSignature';

interface Props {
	onConfirmSignature: (signatureDataUrl: string, source: DrawnSignatureSource) => void;
	disableSavedSignature: boolean;
}

export const SignDrawBody = ({ onConfirmSignature, disableSavedSignature }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	// Witness/public signers are unauthenticated; skip the saved-signature query
	// for them, otherwise the auth-required GET /user/signature 401s and redirects to login.
	const saved = useSavedSignature(!disableSavedSignature);
	const saveMutation = useSaveUserSignature();
	const [showEditor, setShowEditor] = useState(false);

	const showLoader = !disableSavedSignature && saved.isLoading;
	const showPicker = !disableSavedSignature && saved.isPresent && !showEditor;

	const handleUseSaved = () => {
		if (saved.savedSignatureDataUrl) {
			// Saved signatures are stored PNGs from a prior session — treat as
			// canvas so the certificate embeds the bitmap as-is.
			onConfirmSignature(saved.savedSignatureDataUrl, DrawnSignatureSource.CANVAS);
		}
	};

	const handleConfirmFromEditor = async (dataUrl: string, source: DrawnSignatureSource) => {
		if (disableSavedSignature) {
			onConfirmSignature(dataUrl, source);
			return;
		}
		try {
			await saveMutation.mutateAsync(dataUrl);
		} catch {
			showToastV2({
				state: 'error',
				title: tCommonUi('eid_easy_signing.signature_save_failed_title'),
				message: tCommonUi('eid_easy_signing.signature_save_failed_description'),
			});
		}
		onConfirmSignature(dataUrl, source);
	};

	if (showLoader) {
		return (
			<Center mih={200}>
				<Loader />
			</Center>
		);
	}

	if (showPicker) {
		return (
			<SavedSignaturePicker
				savedSignatureDataUrl={saved.savedSignatureDataUrl ?? ''}
				onUseSaved={handleUseSaved}
				onCreateNew={() => setShowEditor(true)}
			/>
		);
	}

	return (
		<SignatureEditor
			confirmLabel={tCommonUi('eid_easy_signing.sign')}
			onConfirm={handleConfirmFromEditor}
			isBusy={saveMutation.isPending}
		/>
	);
};
